// Le bureau : membres et rôles (D-075). Tous les comptes du club et leurs casquettes, les fiches
// sans compte ; une fiche par membre pour donner ou retirer un rôle (bureau, responsable technique,
// coach d'une équipe, supporter), renvoyer l'accès, retirer du club. Le bureau invite pour tous
// les rôles : par e-mail, ou par un lien à envoyer (WhatsApp, SMS).
// « Envoyer les accès à N » (D-103) : toutes les fiches sans compte d'un geste ; l'e-mail importé
// (FBI) est déjà rempli quand on invite une seule personne. Les liens ouvrent sur le sous-domaine du club (D-104).
import { clubMemberList, invite, personInviteLink, removeClubAccount, roleInviteLink, setClubRole, setTeamCoach, trialRequests,
  volunteerRequests, clubVolunteerGroups, type MemberRow, type TrialRequest, type VolunteerGroup, type VolunteerRequest, type LinkRole } from '../lib/api';
import { modOn, type Ctx } from './context';
import { clubGender, playerNoun } from '../domain/labels';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import { emptyGuide } from '../guide/empty';
import { openShare, since } from './share';
import { clubAccessList } from '../lib/reach-api';
import { linksFor } from '../lib/club-links';
import type { AccessRow } from '../domain/access';
import type { ClubLinks } from '../domain/links';
import { accessCard, openSendAccess } from './send-access';

type Filter = 'tous' | 'bureau' | 'coachs' | 'joueurs' | 'parents' | 'supporters' | 'sans';
const FILTERS: [Filter, string][] = [['tous', 'Tous'], ['bureau', 'Bureau'], ['coachs', 'Coachs'], ['joueurs', 'Joueurs'], ['parents', 'Parents'], ['supporters', 'Bénévoles'], ['sans', 'Sans compte']];
const ROLE_LABEL: Record<LinkRole, string> = { admin: 'Bureau', technical: 'Responsable technique', coach: 'Coach', supporter: 'Bénévole' };
/** Les casquettes en mots (base) : « Bénévole » pour qui n'a que le bénévolat (D-103 ; « Supporter » avant). */
const onlyVolunteer = (m: { caps: string | null }) => m.caps === 'Bénévole' || m.caps === 'Supporter';
const capsOf = (m: { caps: string | null }) => (onlyVolunteer(m) ? 'Bénévole' : m.caps ?? '');

const has = (m: MemberRow, f: Filter) => {
  switch (f) {
    case 'tous': return true;
    case 'bureau': return m.roles.includes('admin');
    case 'coachs': return m.coach_teams.length > 0 || m.roles.includes('technical');
    case 'joueurs': return m.people.some(p => p.relation === 'self');
    case 'parents': return m.people.some(p => p.relation === 'parent');
    case 'supporters': return m.kind === 'account' && onlyVolunteer(m);
    case 'sans': return m.kind === 'person';
  }
};
const initial = (s: string) => (s.trim().charAt(0) || '?').toUpperCase();
const state = (m: MemberRow): [string, string] => m.kind === 'person'
  ? (m.link_created ? [`lien envoyé ${since(m.link_created)}`, 'chip-warn'] : ['sans compte', 'chip-mute'])
  : m.code_account ? ['code perso', 'chip-mute'] : m.last_seen ? [`visite ${since(m.last_seen)}`, ''] : [`invitation ${since(m.since)}`, 'chip-warn'];

let filter: Filter = 'tous';
let query = '';

export async function renderMembers(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club || !ctx.full.adminOf.some(c => c.id === club.id)) { mount(root, html`<div class="empty"><p class="empty-title">Réservé au bureau.</p></div>`); return; }
  const [list, trials, vols, access, links] = await Promise.all([
    clubMemberList(club.id).catch(() => [] as MemberRow[]),
    trialRequests([club.id]).catch(() => [] as TrialRequest[]),
    volunteerRequests(club.id).catch(() => [] as VolunteerRequest[]),
    clubAccessList(club.id).catch(() => [] as AccessRow[]),
    linksFor(club.id),
  ]);
  const accessOf = new Map(access.map(a => [a.person_id, a]));
  // Deux sortes de demandes, deux lignes (B5) : chacune ouvre l'écran qui la montre, « à traiter » comme partout.
  const trialReqs = trials.filter(t => t.status === 'nouveau').map(t => `${t.first_name} (${t.source === 'friend' && t.invited_name ? `par ${t.invited_name}` : t.source === 'team' ? 'lien d’équipe' : 'page publique'})`);
  const volReqs = modOn(ctx, 'benevolat') ? vols.filter(v => v.status === 'new').map(v => v.name) : [];
  const reqRow = (href: string, title: string, names: string[]) => html`<a class="card card-row" href="${href}">
        <span class="play-thumb" aria-hidden="true"><span class="ti ti-hand"></span></span>
        <span class="card-main"><span class="card-title">${title}</span><span class="muted small">${names.slice(0, 3).join(' · ')}</span></span>
        <span class="occ-go" aria-hidden="true">›</span></a>`;
  const accounts = list.filter(m => m.kind === 'account').length;
  const reload = () => void renderMembers(root, ctx);
  const draw = () => {
    const q = query.trim().toLowerCase();
    const shown = list.filter(m => has(m, filter) && (!q || `${m.name} ${m.email ?? ''} ${capsOf(m)}`.toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    mount($(root, '#mm-list')!, shown.length ? html`<ul class="iv-list">${shown.map(m => {
      const [label, cls] = state(m);
      return html`<li><button type="button" class="iv-row iv-member" data-m="${m.user_id ?? m.person_id}">
        <span class="iv-av" aria-hidden="true">${initial(m.name)}</span>
        <span class="iv-main"><strong>${m.name}</strong><span class="muted small">${capsOf(m)}</span></span>
        <span class="chip ${cls}">${label}</span></button></li>`;
    })}</ul>${list.length <= 1 && filter === 'tous' && !q ? emptyGuide({ art: 'ti-people', guide: 'mm-empty', title: 'Tout le club dans l’app', tour: 'membres-roles',
        text: `Invite les coachs et le reste du bureau : un lien par personne, par e-mail, WhatsApp ou SMS. Les ${playerNoun(clubGender(ctx.teams), 2)} arrivent par leur coach.` }) : ''}`
      : html`<p class="muted">Personne ici.</p>`);
    for (const b of $$<HTMLButtonElement>(root, '[data-m]')) b.addEventListener('click', () => {
      const m = list.find(x => (x.user_id ?? x.person_id) === b.dataset.m);
      if (m) openMember(ctx, m, reload, links, m.person_id ? accessOf.get(m.person_id) : undefined);
    });
  };

  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${club.name} · ${accounts} ${accounts > 1 ? 'comptes' : 'compte'}</p><h2>Membres et rôles</h2></header>
      ${accessCard(access)}
      ${trialReqs.length ? reqRow('#/essais', `${trialReqs.length} ${trialReqs.length > 1 ? 'demandes d’essai' : 'demande d’essai'} à traiter`, trialReqs) : ''}
      ${volReqs.length ? reqRow('#/benevoles/recrues', `${volReqs.length} ${volReqs.length > 1 ? 'recrues bénévoles' : 'recrue bénévole'} à traiter`, volReqs) : ''}
      <div class="chip-scroll" role="group" aria-label="Filtrer">${FILTERS.filter(([k]) => k !== 'supporters' || modOn(ctx, 'benevolat') || list.some(m => has(m, k))).map(([k, l]) => {
        const n = list.filter(m => has(m, k)).length;
        return html`<button type="button" class="chip-toggle" data-f="${k}" aria-pressed="${String(filter === k)}">${l}${k === 'tous' ? '' : html` <small>${n}</small>`}</button>`;
      })}</div>
      <input class="iv-search" id="mm-q" type="search" placeholder="🔍 Chercher un nom, un e-mail" aria-label="Chercher un membre" value="${query}">
      <div id="mm-list"></div>
      <button type="button" class="btn btn-primary" id="mm-invite" data-guide="mm-invite">＋ Inviter (tous les rôles)</button>
    </section>`);
  draw();
  for (const b of $$<HTMLButtonElement>(root, '[data-f]')) b.addEventListener('click', () => {
    filter = b.dataset.f as Filter;
    $$(root, '[data-f]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    draw();
  });
  $<HTMLInputElement>(root, '#mm-q')!.addEventListener('input', e => { query = (e.target as HTMLInputElement).value; draw(); });
  $(root, '#mm-invite')!.addEventListener('click', () => void openInviteAny(ctx, reload, links));
  $(root, '#mm-access')?.addEventListener('click', () => void openSendAccess(ctx, reload, access));
}

// --- La fiche d'un membre -------------------------------------------------------------------
function openMember(ctx: Ctx, m: MemberRow, reload: () => void, links: ClubLinks, acc?: AccessRow): void {
  const club = ctx.club!;
  const clubTeams = ctx.full.teams.filter(t => t.club_id === club.id);
  if (m.kind === 'person') return openPerson(ctx, m, reload, links, acc);
  const uid = m.user_id!;
  const others = m.roles.some(r => r !== 'supporter') || m.people.length > 0 || m.coach_teams.length > 0;
  const toggle = (key: string, on: boolean, title: string, sub: string, disabled = false): Html => html`
    <li class="iv-role"><span class="iv-main"><strong>${title}</strong><span class="muted small">${sub}</span></span>
      <button type="button" class="iv-switch" role="switch" aria-checked="${String(on)}" aria-label="${title}" data-role="${key}" ${disabled ? 'disabled' : ''}></button></li>`;
  const free = clubTeams.filter(t => !m.coach_teams.some(c => c.id === t.id));
  openSheet(m.name, html`
    <p class="muted small">${m.email ?? (m.code_account ? 'Se connecte avec un code perso' : '')}${m.last_seen ? ` · dernière visite ${since(m.last_seen)}` : ' · aucune visite dans l’app pour l’instant'}</p>
    <h4 class="sub">Ses rôles au club</h4>
    <ul class="iv-list">
      ${m.people.map(p => html`<li class="iv-role"><span class="iv-main"><strong>${p.relation === 'self' ? 'Joue' : `Parent de ${p.first_name}`}${p.teams.length ? ` · ${p.teams.join(', ')}` : ''}</strong>
        <span class="muted small">${p.relation === 'self' ? 'Sa fiche de l’effectif, reliée à son compte' : 'Suit son enfant : planning, réponses, dossier'}</span></span></li>`)}
      ${m.coach_teams.map(t => toggle(`coach:${t.id}`, true, `Coach · ${t.name}`, 'Planning, présences, plays, matchs de l’équipe'))}
      ${toggle('admin', m.roles.includes('admin'), 'Bureau', 'Tout le club : licences, argent, partenaires, rôles', uid === ctx.uid)}
      ${toggle('technical', m.roles.includes('technical'), 'Responsable technique', 'Toutes les équipes, côté sportif')}
      ${modOn(ctx, 'benevolat') || m.roles.includes('supporter') ? toggle('supporter', m.roles.includes('supporter'), 'Bénévole', others ? 'Inutile ici : une autre casquette suffit' : 'Matchs du club, bénévolat', others && !m.roles.includes('supporter')) : ''}
    </ul>
    ${free.length ? html`<form class="inline-form" id="mm-coach" novalidate>
      <select name="team" aria-label="Équipe">${free.map(t => html`<option value="${t.id}">${t.name}</option>`)}</select>
      <button class="btn btn-ghost btn-sm" type="submit">＋ Coach de cette équipe</button></form>` : ''}
    <div class="btn-row">
      ${m.email ? html`<button type="button" class="btn btn-ghost btn-sm" id="mm-resend">Renvoyer l’accès</button>` : ''}
      ${uid !== ctx.uid ? html`<button type="button" class="btn btn-danger-ghost btn-sm" id="mm-out">Retirer du club</button>` : ''}
    </div>
    <p class="hint">Un rôle retiré prend effet tout de suite ; la personne est prévenue. Ses fiches (joueur, parent) restent dans l’effectif.</p>`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-role]')) b.addEventListener('click', async () => {
      const key = b.dataset.role!, on = b.getAttribute('aria-checked') !== 'true';
      b.disabled = true;
      try {
        if (key.startsWith('coach:')) await setTeamCoach(key.slice(6), uid, on);
        else await setClubRole(club.id, uid, key as 'admin' | 'technical' | 'supporter', on);
        b.setAttribute('aria-checked', String(on));
        toast(on ? 'Rôle donné' : 'Rôle retiré');
        if (key.startsWith('coach:') && !on) b.closest('li')?.remove();
      } catch (e) { toast(String((e as Error).message).includes('soi-même') ? 'Pas ton propre rôle de bureau.' : 'Impossible.', 'ko'); }
      finally { b.disabled = false; }
    });
    $<HTMLFormElement>(sheet, '#mm-coach')?.addEventListener('submit', async e => {
      e.preventDefault();
      const team = String(new FormData(e.target as HTMLFormElement).get('team') ?? '');
      try { await setTeamCoach(team, uid, true); toast('Coach ajouté'); close(); } catch { toast('Impossible.', 'ko'); }
    });
    $(sheet, '#mm-resend')?.addEventListener('click', () => {
      const app = links.app;
      openShare('Renvoyer l’accès', `À envoyer à ${m.name} : l’adresse de l’app et l’e-mail avec lequel se connecter.`,
        `Bonjour ${m.name}, ton accès à l’app du ${club.name} : ${app} — connecte-toi avec ton adresse ${m.email}, tu reçois un code.`);
    });
    $(sheet, '#mm-out')?.addEventListener('click', async e => {
      if (!armed(e.currentTarget as HTMLElement, `Retirer ${m.name} du club ?`)) return;
      try { await removeClubAccount(club.id, uid); toast(`${m.name} ne fait plus partie du club`); close(); } catch { toast('Impossible.', 'ko'); }
    });
  }, reload);
}

/** Une fiche de l'effectif sans compte : son lien, celui de ses parents, ou un e-mail — celui de l'import
 *  déjà rempli (l'e-mail des parents pour un mineur, le sien pour un adulte, D-103). */
function openPerson(ctx: Ctx, m: MemberRow, reload: () => void, links: ClubLinks, acc?: AccessRow): void {
  const p = m.people[0]!;
  const club = ctx.club!;
  const team = p.teams[0] ?? '';
  const minor = acc?.minor ?? false;
  const prefill = (minor ? acc?.parent_email ?? acc?.email : acc?.email) ?? '';
  const known = [acc?.phone ? `tél. ${acc.phone}` : '', acc?.email && !minor ? acc.email : '',
    acc?.parent_name || acc?.parent_email || acc?.parent_phone ? `parent : ${[acc.parent_name, acc.parent_phone, acc.parent_email].filter(Boolean).join(' · ')}` : ''].filter(Boolean);
  openSheet(m.name, html`
    <p class="muted small">${capsOf(m)} · pas encore de compte${m.link_created ? ` · lien envoyé ${since(m.link_created)}` : ''}</p>
    ${known.length ? html`<p class="small">Import : ${known.join(' · ')}</p>` : ''}
    <div class="btn-row">
      <button type="button" class="btn ${minor ? 'btn-ghost' : 'btn-primary'} btn-sm" data-link="self">Son lien</button>
      <button type="button" class="btn ${minor ? 'btn-primary' : 'btn-ghost'} btn-sm" data-link="parent">Lien pour ses parents</button>
    </div>
    <form class="inline-form" id="mm-mail" novalidate>
      <input name="email" type="email" inputmode="email" value="${prefill}" placeholder="${minor ? 'e-mail d’un parent' : 'ou son e-mail'}" aria-label="${minor ? 'E-mail d’un parent' : 'Son e-mail'}">
      <button class="btn btn-ghost btn-sm" type="submit">${minor ? 'Inviter le parent' : 'Inviter'}</button>
    </form>
    <p class="hint">${prefill ? 'L’e-mail de l’import est déjà rempli. ' : ''}Le lien est relié à sa fiche ; il ne sert qu’une fois, 7 jours.</p>`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-link]')) b.addEventListener('click', async () => {
      const rel = b.dataset.link as 'self' | 'parent';
      try {
        const l = await personInviteLink(p.id, rel);
        const url = links.invite(l.token);
        close();
        openShare(rel === 'parent' ? `Lien parent de ${p.first_name}` : `Lien de ${p.first_name}`, 'À envoyer en message privé.', rel === 'parent'
          ? `Bonjour, voici votre accès à l’app du ${club.name} pour suivre ${p.first_name}${team ? ` (${team})` : ''} : ${url} — vous donnez votre e-mail, vous recevez un code. Le lien ne sert qu’une fois.`
          : `Salut ${p.first_name} ! Ton accès à l’app du ${club.name}${team ? ` (${team})` : ''} : ${url} — tu donnes ton e-mail, tu reçois un code. Le lien ne sert qu’une fois.`);
      } catch { toast('Lien impossible pour le moment.', 'ko'); }
    });
    $<HTMLFormElement>(sheet, '#mm-mail')!.addEventListener('submit', async e => {
      e.preventDefault();
      const email = String(new FormData(e.target as HTMLFormElement).get('email') ?? '');
      const r = await invite(minor ? 'parent' : 'player', p.id, email, undefined, undefined, undefined, links.app);
      if (r.error) { toast(r.error === 'input' ? 'Adresse invalide.' : r.error === 'account_in_use' ? 'Adresse déjà prise par une autre fiche du club.' : 'Invitation impossible.', 'ko'); return; }
      toast(r.status === 'invited' ? (minor ? 'Invitation envoyée au parent' : 'Invitation envoyée') : 'Compte existant relié'); close();
    });
  }, reload);
}

// --- Inviter (tous les rôles) -----------------------------------------------------------------
async function openInviteAny(ctx: Ctx, reload: () => void, links: ClubLinks): Promise<void> {
  const club = ctx.club!;
  const teams = ctx.full.teams.filter(t => t.club_id === club.id);
  const groups = await clubVolunteerGroups(club.id).catch(() => [] as VolunteerGroup[]);
  let role: LinkRole | 'player' = 'coach';
  openSheet('Inviter', html`
    <div class="chip-scroll" role="radiogroup" aria-label="Rôle">${([['coach', 'Coach'], ['admin', 'Bureau'], ['technical', 'Resp. technique'], ['supporter', 'Bénévole'], ['player', 'Joueur ou parent']] as [string, string][])
      .filter(([k]) => k !== 'supporter' || modOn(ctx, 'benevolat'))   // D-122 : un bénévole, avec le bénévolat
      .map(([k, l]) => html`<button type="button" class="chip-toggle" role="radio" data-r="${k}" aria-pressed="${String(k === role)}">${l}</button>`)}</div>
    <div id="mm-form"></div>`, (sheet, close) => {
    const draw = () => {
      const el = $(sheet, '#mm-form')!;
      if (role === 'player') {
        mount(el, html`<p class="muted">Un joueur ou un parent reçoit un lien relié à sa fiche : depuis l’équipe.</p>
          <ul class="iv-list">${teams.map(t => html`<li><a class="iv-row" href="#/inviter/equipe/${t.id}"><span class="iv-main"><strong>${t.name}</strong></span><span class="occ-go" aria-hidden="true">›</span></a></li>`)}</ul>`);
        for (const a of $$<HTMLAnchorElement>(el, 'a')) a.addEventListener('click', () => close());
        return;
      }
      mount(el, html`
        <form class="form" id="f-any" novalidate>
          <label class="field"><span>Prénom</span><input name="name" maxlength="60" placeholder="ex. Paul"></label>
          ${role === 'coach' ? html`<label class="field"><span>Équipe</span><select name="team">${teams.map(t => html`<option value="${t.id}">${t.name}</option>`)}</select></label>` : ''}
          ${role === 'supporter' && groups.length ? html`<label class="field"><span>Groupe</span><select name="group"><option value="">Pas de groupe</option>${groups.map(g => html`<option value="${g.id}">${g.emoji} ${g.name}</option>`)}</select></label>` : ''}
          <label class="field"><span>E-mail (pour l’inviter par e-mail)</span><input name="email" type="email" inputmode="email" placeholder="prenom@exemple.fr"></label>
          <p class="form-msg" role="alert"></p>
          <div class="btn-row">
            <button class="btn btn-primary btn-sm" type="submit" data-how="mail">Envoyer par e-mail</button>
            <button class="btn btn-ghost btn-sm" type="submit" data-how="link">Créer un lien (WhatsApp, SMS)</button>
          </div>
          <p class="hint">${ROLE_LABEL[role]} : ${role === 'admin' ? 'tout le club, rôles compris.' : role === 'technical' ? 'toutes les équipes, côté sportif.' : role === 'coach' ? 'son équipe : planning, présences, plays, matchs.' : 'matchs du club et bénévolat.'} Le lien ne sert qu’une fois, 7 jours.</p>
        </form>`);
      const f = $<HTMLFormElement>(el, '#f-any')!;
      f.addEventListener('submit', async e => {
        e.preventDefault();
        const how = ((e as SubmitEvent).submitter as HTMLButtonElement | null)?.dataset.how ?? 'link';
        const d = new FormData(f);
        const name = String(d.get('name') ?? '').trim() || null;
        const team = String(d.get('team') ?? '') || null;
        const group = String(d.get('group') ?? '') || null;
        const email = String(d.get('email') ?? '').trim();
        const msg = $(f, '.form-msg')!;
        const r = role as LinkRole;
        try {
          if (how === 'mail') {
            if (!email) { msg.textContent = 'L’e-mail, pour l’inviter par e-mail. Sinon : créer un lien.'; return; }
            const res = await invite(r, r === 'coach' ? team! : club.id, email, undefined, name ?? undefined, group ?? undefined, links.app);
            if (res.error) { msg.textContent = res.error === 'input' ? 'Adresse invalide.' : 'Invitation impossible.'; return; }
            toast(res.status === 'invited' ? 'Invitation envoyée par e-mail' : 'Compte existant : rôle donné'); close(); reload();
            return;
          }
          const l = await roleInviteLink(club.id, r, { team, group, name });
          const teamName = teams.find(t => t.id === team)?.name;
          const what = r === 'coach' ? `coach des ${teamName}` : r === 'admin' ? 'membre du bureau' : r === 'technical' ? 'responsable technique' : 'bénévole';
          close();
          openShare(`Lien ${ROLE_LABEL[r].toLowerCase()}`, 'À envoyer en message privé. Le lien ne sert qu’une fois, 7 jours.',
            r === 'supporter'
              ? `${name ?? 'Bonjour'}, merci de nous donner un coup de main au ${club.name} ! Ton accès à l’app des bénévoles : ${links.invite(l.token)} — tu donnes ton e-mail, tu reçois un code.`
              : `Bonjour${name ? ` ${name}` : ''}, votre accès ${what} à l’app du ${club.name} : ${links.invite(l.token)} — vous donnez votre e-mail, vous recevez un code. Le lien ne sert qu’une fois.`);
        } catch { msg.textContent = 'Impossible pour le moment.'; }
      });
    };
    draw();
    for (const b of $$<HTMLButtonElement>(sheet, '[data-r]')) b.addEventListener('click', () => {
      role = b.dataset.r as LinkRole | 'player';
      $$(sheet, '[data-r]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      draw();
    });
  });
}
